/**
 * Single source of truth for every SUCCESS / FAILED / IN_PROGRESS / ERROR message the upload
 * pipeline surfaces in the modal's summary panel and inline error details.
 *
 * Wording is deliberately plain-English and non-technical — a finance analyst without engineering
 * context is the primary reader. When rewording, keep SUCCESS / FAILED symmetric so the summary
 * reads consistently regardless of state.
 *
 * Any hardcoded message string outside this file is a bug — every caller must reference these
 * constants. This lets a future reword be a single-file change rather than a codebase sweep.
 */
const ForecastValidationMessages = {
  HEADER_VALIDATION_DEFAULT_MESSAGE: 'The header fields do not match.',
  HEADER_VALIDATION_FAILED: "Column headers don't match the template",
  HEADER_VALIDATION_N_PROGRESS: 'Validating file format & header',
  HEADER_VALIDATION_SUCCESS: 'Column headers match the template',
  HEADER_VALIDATION_ERROR: 'Header validation error',

  SKIPPED_DUE_TO_HEADER_FAILURE: 'Skipped due to header validation failure',

  NO_MODIFIED_ROWS: 'No modified rows to validate',

  VALIDATION_ERROR: 'Validation error',

  ERROR_READING_FILE: 'Error reading file',
  TRANSFORMATION_FAILED: 'File transformation failed',
  TRANSFORMATION_FAILED_DETAILS: 'Unable to to transform certain data. Upload aborted.',

  MANDATORY_FIELDS_VALIDATION_DEFAULT_MESSAGE: (mandatoryFields: string[]) => `The following fields are required: ${mandatoryFields.join(', ')}.`,
  MANDATORY_FIELDS_VALIDATION_IN_PROGRESS: 'Validating required fields',
  MANDATORY_FIELDS_VALIDATION_SUCCESS: 'All required columns filled in',
  MANDATORY_FIELDS_VALIDATION_FAILED: 'Some required columns are empty',
  MANDATORY_FIELDS_VALIDATION_ERROR: 'Required field validation error',

  NON_EDITABLE_FIELD_VALIDATION_DEFAULT: 'Modification Attempt on a Non-Editable Field',
  NON_EDITABLE_FIELD_VALIDATION_IN_PROGRESS: 'Validating non-editable fields',
  NON_EDITABLE_FIELD_VALIDATION_SUCCESS: 'No locked fields were changed',
  NON_EDITABLE_FIELD_VALIDATION_FAILED: 'Locked fields were changed',
  NON_EDITABLE_FIELD_VALIDATION_ERROR: 'Non-editable field validation error',

  SEGMENTS_VALIDATION_DEFAULT: 'Ensure that Corp Segments have valid data.',
  SEGMENTS_VALIDATION_IN_PROGRESS: 'Validating segments',
  SEGMENTS_VALIDATION_IN_SUCCESS: 'Segments look good',
  SEGMENTS_VALIDATION_IN_FAILED: "Some segments aren't valid",
  SEGMENTS_VALIDATION_IN_ERROR: 'Segment validation error',

  UNAUTHORIZED_ROWS_VALIDATION_DEFAULT: 'Update only the data you own.',
  UNAUTHORIZED_ROWS_VALIDATION_IN_PROGRESS: 'Validating unauthorized modifications',
  UNAUTHORIZED_ROWS_VALIDATION_SUCCESS: 'You only changed your own rows',
  UNAUTHORIZED_ROWS_VALIDATION_FAILED: "You changed rows you don't own",
  UNAUTHORIZED_ROWS_VALIDATION_ERROR: 'Authorization validation error',

  XVT_SOURCED_ROWS_VALIDATION_DEFAULT: 'Rows synced from xVT Planning are read-only and cannot be modified.',
  XVT_SOURCED_ROWS_VALIDATION_IN_PROGRESS: 'Validating xVT-sourced rows',
  XVT_SOURCED_ROWS_VALIDATION_SUCCESS: 'No xVT rows were changed',
  XVT_SOURCED_ROWS_VALIDATION_FAILED: "xVT rows can't be changed here",
  XVT_SOURCED_ROWS_VALIDATION_ERROR: 'Error validating xVT-sourced rows',

  DUPLICATE_RECORDS_VALIDATION_DEFAULT: `Duplicate records found in the data.`,
  DUPLICATE_RECORDS_VALIDATION_IN_PROGRESS: 'Checking for duplicate records',
  DUPLICATE_RECORDS_FOUND: 'Duplicate rows in the file',
  NO_DUPLICATE_RECORDS: 'No duplicate rows',
  DUPLICATE_RECORDS_VALIDATION_ERROR: 'Error checking for duplicate records',

  EXISTING_RECORDS_VALIDATION_DEFAULT: `Checking if records already exist`,
  EXISTING_RECORDS_VALIDATION_IN_PROGRESS: 'Checking if records already exist',
  EXISTING_RECORDS_FOUND: 'Some rows already exist',
  NO_EXISTING_RECORDS: 'No matches with existing rows',
  EXISTING_RECORDS_VALIDATION_ERROR: 'Error checking for existing records',

  FORECAST_MONTH_DATA_VALIDATION_DEFAULT: 'Forecast months should have valid values',
  FORECAST_MONTH_DATA_VALIDATION_IN_PROGRESS: 'Validating forecast month data',
  FORECAST_MONTH_DATA_VALIDATION_SUCCESS: 'Monthly numbers look good',
  FORECAST_MONTH_DATA_VALIDATION_FAILED: "Monthly numbers aren't valid",
  FORECAST_MONTH_DATA_VALIDATION_ERROR: 'Error validating forecast month data',

  ACCOUNT_BUDGET_TYPE_MAPPING_VALIDATION_DEFAULT: 'Account and Budget Type should be valid combination',
  ACCOUNT_BUDGET_TYPE_MAPPING_VALIDATION_IN_PROGRESS: 'Validating Account and Budget Type combination',
  ACCOUNT_BUDGET_TYPE_MAPPING_VALIDATION_SUCCESS: 'Account matches its Budget Type',
  ACCOUNT_BUDGET_TYPE_MAPPING_VALIDATION_FAILED: "Account doesn't match its Budget Type",
  ACCOUNT_BUDGET_TYPE_MAPPING_VALIDATION_ERROR: 'Error validating Account and Budget Type combination',

  REV_GP_MAPPING_VALIDATION_DEFAULT: 'Channel and Product Line should be valid combination',
  REV_GP_MAPPING_VALIDATION_IN_PROGRESS: 'Validating Channel and Product Line combination',
  REV_GP_MAPPING_VALIDATION_SUCCESS: 'Channel and Product Line match',
  REV_GP_MAPPING_VALIDATION_FAILED: "Channel and Product Line don't match",
  REV_GP_MAPPING_VALIDATION_ERROR: 'Error validating Channel and Product Line combination',
  REV_GP_MAPPING_UNAVAILABLE: 'Channel and Product Line mapping unavailable; validation skipped',

  BUDGET_OWNER_AND_COST_CENTER_VALIDATION_DEFAULT: 'Budget Owner and Cost Center should be valid combination',
  BUDGET_OWNER_AND_COST_CENTER_VALIDATION_IN_PROGRESS: 'Validating Budget Owner and Cost Center combination',
  BUDGET_OWNER_AND_COST_CENTER_VALIDATION_SUCCESS: 'Budget owner has access to the cost center',
  BUDGET_OWNER_AND_COST_CENTER_VALIDATION_FAILED: "Budget owner doesn't have access to the cost center",
  BUDGET_OWNER_AND_COST_CENTER_VALIDATION_ERROR: 'Error validating Budget Owner and Cost Center combination',

  CC_AUTHORIZATION_VALIDATION_DEFAULT: 'Unauthorized Cost Center(s) found in the file',
  CC_AUTHORIZATION_VALIDATION_IN_PROGRESS: 'Validating Cost Center(s) in file',
  CC_AUTHORIZATION_VALIDATION_SUCCESS: 'Cost centers look good',
  CC_AUTHORIZATION_VALIDATION_FAILED: "Cost center you can't access",
  CC_AUTHORIZATION_VALIDATION_ERROR: 'Error validating Cost Center(s) authorization',

  BUDGET_OWNER_VALIDATION_DEFAULT: 'Unauthorized Budget Owner(s) found in the file',
  BUDGET_OWNER_VALIDATION_IN_PROGRESS: 'Validating Budget Owner(s) in file',
  BUDGET_OWNER_VALIDATION_SUCCESS: 'Budget owners look good',
  BUDGET_OWNER_VALIDATION_FAILED: "Budget owner you can't use",
  BUDGET_OWNER_VALIDATION_ERROR: 'Error validating Budget Owner(s) authorization',

  DMM_SEGMENTS_VALIDATION_FAILED: "Some DMM segments aren't valid",
  DMM_SEGMENTS_VALIDATION_SUCCESS: 'DMM segments look good',
  DMM_SEGMENTS_VALIDATION_DEFAULT: 'Validating DMM business segment combinations',
  DMM_SEGMENTS_VALIDATION_IN_PROGRESS: 'Validating DMM business segment combinations...',

  //DMM works on BO + PL level
  DMM_BUDGET_OWNER_AND_COST_CENTER_VALIDATION_DEFAULT: 'Budget Owner and Product Line should be valid combination',
  DMM_BUDGET_OWNER_AND_COST_CENTER_VALIDATION_IN_PROGRESS: 'Validating Budget Owner and Product Line combination',
  DMM_BUDGET_OWNER_AND_COST_CENTER_VALIDATION_SUCCESS: 'Budget owner has access to the product line',
  DMM_BUDGET_OWNER_AND_COST_CENTER_VALIDATION_FAILED: "Budget owner doesn't have access to the product line",
  DMM_BUDGET_OWNER_AND_COST_CENTER_VALIDATION_ERROR: 'Error validating Budget Owner and Product Line combination',

  FILE_CHANGE_DETECTION_DEFAULT: 'Detecting changes between uploaded file and current data',
  // Dynamic detail message still used by the validator itself for debug/error trails; the
  // summary panel uses the shorter static SUCCESS_LABEL below.
  FILE_CHANGE_DETECTION_SUCCESS: (modifiedCount: number, newCount: number) => {
    const parts: string[] = [];
    if (modifiedCount > 0) parts.push(`${modifiedCount} modified row${modifiedCount > 1 ? 's' : ''}`);
    if (newCount > 0) parts.push(`${newCount} new row${newCount > 1 ? 's' : ''}`);
    return `Changes detected: ${parts.join(' and ')}`;
  },
  FILE_CHANGE_DETECTION_SUCCESS_LABEL: 'Your file has changes to review',
  FILE_CHANGE_DETECTION_FAILED: 'Your file has no changes to submit',

  // Final pre-submit gate (Gate B): shown when every modified preview row has been fixed/reset
  // back to match the current system data, leaving nothing real to submit. Distinct from
  // FILE_CHANGE_DETECTION_FAILED (which fires at upload time on the original file) — this one
  // fires at submit time after the user's inline edits, resets, and removals.
  NO_SUBMITTABLE_CHANGES: 'No changes to submit. All modifications now match the current data.',

  // Currency field — previously inline strings in CurrencyFieldsValidationService.
  CURRENCY_FIELD_VALIDATION_DEFAULT: 'Currency field validation',
  CURRENCY_FIELD_VALIDATION_SUCCESS: 'Currency values look good',
  CURRENCY_FIELD_VALIDATION_FAILED: "Currency values aren't valid",
  CURRENCY_FIELD_VALIDATION_ERROR: 'Currency field validation error',

  // Budget editable months — previously local constants in BudgetEditableMonthsValidationService.
  BUDGET_EDITABLE_MONTHS_VALIDATION_SUCCESS: 'Only editable months were changed',
  BUDGET_EDITABLE_MONTHS_VALIDATION_FAILED: "You changed a month you can't edit"
};

export default ForecastValidationMessages;
